/**
 * CSS Validator - Detects invalid CSS before injection
 *
 * Creates a temporary style element, inserts CSS, and checks
 * if rules were parsed correctly by the browser.
 */

export interface CSSValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    parsedRuleCount: number;
    expectedRuleCount: number;
}

/**
 * Count top-level CSS rules using depth tracking.
 * This correctly handles nested CSS (@media, @supports) by only counting
 * rules when brace depth returns to zero.
 */
function countTopLevelRules(input: string): number {
    let depth = 0;
    let count = 0;
    let inSingle = false;
    let inDouble = false;

    for (let i = 0; i < input.length; i++) {
        const char = input[i];
        const prev = input[i - 1];

        // Track string context to avoid counting braces inside strings
        if (char === "'" && prev !== '\\' && !inDouble) {
            inSingle = !inSingle;
        } else if (char === '"' && prev !== '\\' && !inSingle) {
            inDouble = !inDouble;
        }

        if (inSingle || inDouble) {
            continue;
        }

        if (char === '{') {
            depth += 1;
        } else if (char === '}') {
            depth = Math.max(0, depth - 1);
            if (depth === 0) {
                count += 1;
            }
        }
    }

    return count;
}

/**
 * Detect nested @media/@supports blocks which are not supported by the CSS scoper.
 * Returns a warning message if nested at-rules are found, null otherwise.
 */
function detectNestedAtRules(css: string): string | null {
    let atRuleDepth = 0;
    let inString = false;
    let stringChar = '';

    for (let i = 0; i < css.length; i++) {
        const char = css[i];
        const prev = css[i - 1];

        // Track string context
        if ((char === '"' || char === "'") && prev !== '\\') {
            if (!inString) {
                inString = true;
                stringChar = char;
            } else if (char === stringChar) {
                inString = false;
            }
        }

        if (inString) continue;

        // Check for @media or @supports
        if (char === '@') {
            const remaining = css.slice(i, i + 15).toLowerCase();
            if (remaining.startsWith('@media') || remaining.startsWith('@supports')) {
                if (atRuleDepth > 0) {
                    return 'Nested @media/@supports blocks are not supported and may not be scoped correctly';
                }
            }
        }

        // Track depth of @media/@supports blocks
        if (char === '{') {
            // Check if this brace follows an @media or @supports
            const precedingText = css.slice(Math.max(0, i - 50), i).toLowerCase();
            if (precedingText.match(/@media[^{]*$/) || precedingText.match(/@supports[^{]*$/)) {
                atRuleDepth++;
            }
        } else if (char === '}' && atRuleDepth > 0) {
            atRuleDepth--;
        }
    }

    return null;
}

/**
 * Validate CSS by attempting to parse it in a temporary style element
 */
export function validateCSS(css: string): CSSValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Quick syntax checks
    const openBraces = (css.match(/{/g) || []).length;
    const closeBraces = (css.match(/}/g) || []).length;

    if (openBraces !== closeBraces) {
        errors.push(`Mismatched braces: ${openBraces} opening, ${closeBraces} closing`);
    }

    // Count expected top-level rules (handles nested @media/@supports correctly)
    const cssWithoutUrls = css.replace(/url\([^)]*\)/g, 'url()');
    const expectedRuleCount = countTopLevelRules(cssWithoutUrls);

    // Check for line breaks inside url() - common issue
    const urlMatches = css.match(/url\s*\([^)]*\)/g) || [];
    for (const urlMatch of urlMatches) {
        if (urlMatch.includes('\n')) {
            errors.push('Line break detected inside url() - URLs must be on a single line');
        }
    }

    // Check for unclosed quotes
    let inSingleQuote = false;
    let inDoubleQuote = false;
    for (let i = 0; i < css.length; i++) {
        const char = css[i];
        const prev = css[i - 1];

        if (char === "'" && prev !== '\\' && !inDoubleQuote) {
            inSingleQuote = !inSingleQuote;
        } else if (char === '"' && prev !== '\\' && !inSingleQuote) {
            inDoubleQuote = !inDoubleQuote;
        }
    }
    if (inSingleQuote) {
        errors.push('Unclosed single quote in CSS');
    }
    if (inDoubleQuote) {
        errors.push('Unclosed double quote in CSS');
    }

    // Check for nested @media/@supports (not supported by CSS scoper)
    const nestedAtRuleWarning = detectNestedAtRules(css);
    if (nestedAtRuleWarning) {
        warnings.push(nestedAtRuleWarning);
    }

    // Browser-based validation: create temp style element
    let parsedRuleCount = 0;
    if (typeof document !== 'undefined') {
        const tempStyle = document.createElement('style');
        tempStyle.textContent = css;
        document.head.appendChild(tempStyle);

        try {
            if (tempStyle.sheet) {
                parsedRuleCount = tempStyle.sheet.cssRules.length;

                // Check each rule for empty declarations
                for (let i = 0; i < tempStyle.sheet.cssRules.length; i++) {
                    const rule = tempStyle.sheet.cssRules[i] as CSSStyleRule;
                    if (rule.style && rule.style.length === 0) {
                        warnings.push(`Rule "${rule.selectorText}" has no valid properties`);
                    }
                }
            }
        } catch (e) {
            errors.push(`CSS parsing error: ${(e as Error).message}`);
        } finally {
            document.head.removeChild(tempStyle);
        }

        // Check if all rules were parsed
        if (parsedRuleCount < expectedRuleCount) {
            errors.push(
                `Only ${parsedRuleCount} of ${expectedRuleCount} rules were parsed - CSS may contain syntax errors`
            );
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        parsedRuleCount,
        expectedRuleCount,
    };
}

/**
 * Quick check if CSS is valid (returns boolean)
 */
export function isValidCSS(css: string): boolean {
    return validateCSS(css).isValid;
}
